import React from 'react'
import PropTypes from 'prop-types'
import Highcharts from 'highcharts'
import HighchartsReact from 'highcharts-react-official'
import HighchartsExporting from 'highcharts/modules/exporting'
import HighchartsOfflineExporting from 'highcharts/modules/offline-exporting'
import { titleize } from 'helpers/utils'
import { XAxisColumnOption, YAxisColumnOption } from './chart-options'
import { drillDownPlotOptions } from './drilldown'

HighchartsExporting(Highcharts)
HighchartsOfflineExporting(Highcharts)

function ScatterPlot({
  points,
  tooltipFormatter,
  xAxisLabel,
  yAxisLabel,
  xAxisColumn,
  yAxisColumn,
  colors = null,
  chartRef = null,
  onPointClick = null
}) {
  const data = points.map((p) =>
    typeof p.z !== 'undefined' ? [p.x, p.y, p.z] : [p.x, p.y]
  )

  // Determine if axes should be datetime type based on column display_as property
  const isXAxisDate = xAxisColumn?.display_as === 'date'
  const isYAxisDate = yAxisColumn?.display_as === 'date'

  const options = {
    ...(colors && { colors }), // Apply brand colors if provided
    chart: {
      type: 'scatter',
      zoomType: 'xy'
    },
    title: { text: '' },
    xAxis: {
      title: { text: titleize(xAxisLabel) },
      crosshair: true,
      ...(isXAxisDate && { type: 'datetime' })
    },
    yAxis: {
      title: { text: titleize(yAxisLabel) },
      crosshair: true,
      ...(isYAxisDate && { type: 'datetime' })
    },
    tooltip: {
      formatter: function () {
        if (tooltipFormatter) {
          return tooltipFormatter(this.point)
        }

        // Format x value based on whether it's a date
        const xValue = isXAxisDate
          ? Highcharts.dateFormat('%Y-%m-%d', this.point.x)
          : this.point.x

        // Format y value based on whether it's a date
        const yValue = isYAxisDate
          ? Highcharts.dateFormat('%Y-%m-%d', this.point.y)
          : this.point.y

        const zVal =
          typeof this.point.z !== 'undefined'
            ? `<br/>Size: ${this.point.z}`
            : ''
        return `<b>${titleize(xAxisLabel)}:</b> ${xValue}<br/><b>${titleize(yAxisLabel)}:</b> ${yValue}${zVal}`
      }
    },
    series: [
      {
        data,
        ...(colors ? {} : { color: '#5c80bb' }) // Use default color only if no brand colors provided
      }
    ],
    credits: { enabled: false },
    plotOptions: drillDownPlotOptions(onPointClick),
    legend: {
      enabled: false
    },
    exporting: {
      enabled: true,
      filename: 'scatter-plot',
      fallbackToExportServer: false,
      libURL: '', // Prevent loading any external libraries
      buttons: {
        contextButton: {
          enabled: false
        }
      }
    }
  }

  return <HighchartsReact highcharts={Highcharts} options={options} ref={chartRef} updateArgs={[true, true, false]} />
}

ScatterPlot.propTypes = {
  points: PropTypes.arrayOf(
    PropTypes.shape({
      x: PropTypes.number.isRequired,
      y: PropTypes.number.isRequired,
      z: PropTypes.number
    })
  ).isRequired,
  tooltipFormatter: PropTypes.func,
  xAxisLabel: PropTypes.string,
  yAxisLabel: PropTypes.string,
  xAxisColumn: PropTypes.object,
  yAxisColumn: PropTypes.object,
  colors: PropTypes.arrayOf(PropTypes.string),
  onPointClick: PropTypes.func,
  chartRef: PropTypes.object
}

// Chart configuration for scatter plots
export const scatterPlotConfig = {
  requiredFields: ['title', 'xAxisColumn', 'yAxisColumn'],

  validate: (formData) => {
    const errors = {}
    if (!formData.xAxisColumn) {
      errors.xAxisColumn = 'X-axis column is required for scatter plots'
    }
    if (!formData.yAxisColumn) {
      errors.yAxisColumn = 'Y-axis column is required for scatter plots'
    }
    return errors
  },

  isQueryEnabled: (formData, hasChartId) => {
    return !!(hasChartId && formData.xAxisColumn && formData.yAxisColumn)
  },

  buildChartParams: (formData) => ({
    x_axis_column_id: formData.xAxisColumn,
    y_axis_column_id: formData.yAxisColumn
  }),

  // Build query params for API request
  buildQueryParams: (baseParams, formData, columns) => {
    const xAxisCol = columns?.find((c) => c.id === formData.xAxisColumn)
    const yAxisCol = columns?.find((c) => c.id === formData.yAxisColumn)
    const params = { ...baseParams }
    params.scatter_x = xAxisCol?.database_column_name
    params.scatter_y = yAxisCol?.database_column_name
    return params
  },

  // Render chart-specific options in the modal form
  renderOptions: ({
    Form,
    formState,
    setters,
    validationErrors,
    hasSubmitted,
    getFilteredColumns,
    getColumnDisplayName,
    municipality
  }) => {
    const { xAxisColumn, yAxisColumn } = formState
    const { setXAxisColumn, setYAxisColumn, setValidationErrors } = setters
    const filteredColumns = getFilteredColumns()

    // Create a wrapper function to match the expected signature
    const getGroupingColumns = () => filteredColumns

    return (
      <div className="mb-3">
        <XAxisColumnOption
          Form={Form}
          xAxisColumn={xAxisColumn}
          setXAxisColumn={setXAxisColumn}
          validationErrors={validationErrors}
          setValidationErrors={setValidationErrors}
          hasSubmitted={hasSubmitted}
          getGroupingColumns={getGroupingColumns}
          getColumnDisplayName={getColumnDisplayName}
          municipality={municipality}
        />

        <YAxisColumnOption
          Form={Form}
          yAxisColumn={yAxisColumn}
          setYAxisColumn={setYAxisColumn}
          yAxisAggregation={null}
          setYAxisAggregation={() => {}}
          validationErrors={validationErrors}
          setValidationErrors={setValidationErrors}
          hasSubmitted={hasSubmitted}
          getValueColumns={getGroupingColumns}
          getAggregationOptions={() => []}
          getColumnDisplayName={getColumnDisplayName}
          municipality={municipality}
        />
      </div>
    )
  }
}

export default React.memo(ScatterPlot)
